import React, { useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import {
  AudioFileFormat,
  StudioProjectVersion,
} from '../../../../server/graphql/__generated.types';
import { useUserContext } from '../../../context/user/UserContext';
import useClipDownloader from '../../Download/useClipDownloader';
import useDownloadLimitPrompt from '../../Download/useDownloadLimitPrompt';
import LoadingIcon from '../../LoadingIndicators/CircularLoader';
import { usePromptsContext } from '../../../context/prompts/PromptsContext';

export type BulkClipDownloadProps = {
  clipIds: string[];
  className?: string;
  hideLabel?: boolean;
  setDownloadError?: (downloadError: string) => void;
  audioType: 'clips' | 'takes';
  projectId: string;
  studioVersion: StudioProjectVersion;
  disabled?: boolean;
  children?: React.ReactNode;
  takesSampleRatesById?: Record<string, number>;
  singleSelectedSectionName?: string | null;
  projectName?: string | null;
  ariaLabel?: string | null;
  title?: string | null;
  bulkPauseLength?: number | null;
  totalDurationMs?: number;
};

const useStyles = makeStyles()(theme => ({
  button: {
    padding: 0,
  },
}));

/**
 * This component allows the user to download all the audio files in a project into a single zip
 * archive.
 * @see [index.tsx] for usage/import of this component.
 */
const BulkClipDownloadButton = (props: BulkClipDownloadProps) => {
  const {
    clipIds,
    className,
    hideLabel,
    setDownloadError,
    projectId,
    studioVersion,
    audioType,
    disabled,
    children,
    takesSampleRatesById,
    singleSelectedSectionName,
    projectName,
    ariaLabel,
    title,
    bulkPauseLength,
    totalDurationMs,
  } = props;
  const { classes, cx } = useStyles();
  const { data: userQuery } = useUserContext();
  const [, dispatchPrompt] = usePromptsContext();
  const isDownloadBlocked = useDownloadLimitPrompt();
  const { onDownloadClips, onDownloadTakes } = useClipDownloader({
    onDownloadStart: () => {
      setDownloading(true);
    },
    onDownloadComplete: () => {
      setDownloading(false);
    },
  });
  const [downloading, setDownloading] = useState<boolean>(false);

  const handleClipsDownload = useCallback(() => {
    if (isDownloadBlocked(totalDurationMs ?? 0)) return;

    if (userQuery?.user.settings.globalAudioFileFormatSelected) {
      if (clipIds) {
        if (audioType === 'clips') {
          onDownloadClips({
            clipIds,
            audioFileFormat:
              userQuery?.user.settings.globalAudioFileFormat ??
              AudioFileFormat.Mp3,
            includeScriptFormats:
              userQuery?.user.settings.includeClipScriptFormats ?? undefined,
          });
        } else {
          onDownloadTakes({
            takeIds: clipIds,
            audioFileFormat:
              userQuery?.user.settings.globalAudioFileFormat ??
              AudioFileFormat.Mp3,
            includeScriptFormats:
              userQuery?.user.settings.includeClipScriptFormats ?? undefined,
            source: 'bulk',
            projectId,
          });
        }
      }
    } else {
      dispatchPrompt({
        type: 'OpenDownloadPrompt',
        payload: {
          downloadType: audioType,
          clipIds,
          setDownloadError,
          projectId,
          studioVersion,
          ...(audioType === 'takes' && takesSampleRatesById
            ? { takesSampleRatesById }
            : {}),
          sectionName: singleSelectedSectionName,
          projectName,
          bulkPauseLength,
        },
      });
    }
  }, [
    isDownloadBlocked,
    totalDurationMs,
    userQuery?.user.settings.globalAudioFileFormatSelected,
    userQuery?.user.settings.globalAudioFileFormat,
    userQuery?.user.settings.includeClipScriptFormats,
    clipIds,
    audioType,
    onDownloadClips,
    onDownloadTakes,
    projectId,
    dispatchPrompt,
    setDownloadError,
    studioVersion,
    takesSampleRatesById,
    singleSelectedSectionName,
    projectName,
    bulkPauseLength,
  ]);

  const downloadButtonLabel = hideLabel ? undefined : 'Download';

  return (
    <Button
      variant="icon"
      title={title ?? ''}
      aria-label={ariaLabel ?? 'download'}
      label={downloadButtonLabel}
      disabled={downloading || disabled}
      onClick={handleClipsDownload}
      className={cx(classes.button, className)}
    >
      <>
        {downloading ? <LoadingIcon /> : <Icon type="download" />}
        {children}
      </>
    </Button>
  );
};

export default BulkClipDownloadButton;
